import { Globe } from 'lucide-react';
import { BookingSource } from '@pms/shared';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Shared booking-source badge (rate-parity-readback Flow 4 / 5). Renders the
// OTA identity + "via AxisRooms" + reference for channel bookings, and "Direct"
// otherwise. Used in the reservation detail header and the bill detail header
// so both surfaces stay consistent. The specific OTA lives in
// `channelPayload.ota` (there is no per-OTA enum), so the label reads it from
// the retained payload with a graceful fallback to the generic source label.
// ---------------------------------------------------------------------------

type Payload = Record<string, unknown>;

function asRecord(v: unknown): Payload | undefined {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Payload) : undefined;
}

function firstStr(...vals: unknown[]): string | undefined {
  for (const v of vals) {
    if (v != null && v !== '') return String(v);
  }
  return undefined;
}

const GENERIC_LABEL: Record<string, string> = {
  [BookingSource.AIRBNB]: 'Airbnb',
  [BookingSource.BOOKING_COM]: 'Booking.com',
  [BookingSource.EXPEDIA]: 'Expedia',
  [BookingSource.DIRECT]: 'Direct',
  [BookingSource.MANUAL]: 'Manual',
  [BookingSource.AXIS_ROOMS]: 'AxisRooms',
};

const TONE: Record<string, string> = {
  [BookingSource.AIRBNB]: 'bg-rose-100 text-rose-700',
  [BookingSource.BOOKING_COM]: 'bg-blue-100 text-blue-700',
  [BookingSource.EXPEDIA]: 'bg-yellow-100 text-yellow-700',
  [BookingSource.DIRECT]: 'bg-emerald-100 text-emerald-700',
  [BookingSource.MANUAL]: 'bg-gray-100 text-gray-600',
  [BookingSource.AXIS_ROOMS]: 'bg-indigo-100 text-indigo-700',
};

export interface ResolvedSource {
  label: string;
  ref: string | undefined;
  tone: string;
  viaAxisRooms: boolean;
}

/** Resolve a human label + reference for a booking source + channel payload. */
export function resolveSource(
  source: string,
  channelPayload?: Payload | null,
  externalBookingId?: string | null,
): ResolvedSource {
  const isChannel = source === BookingSource.AXIS_ROOMS;
  if (!isChannel) {
    return {
      label: GENERIC_LABEL[source] ?? source,
      ref: undefined,
      tone: TONE[source] ?? 'bg-gray-100 text-gray-600',
      viaAxisRooms: false,
    };
  }
  const payload = asRecord(channelPayload) ?? {};
  const bd = asRecord(payload.BookingDetails) ?? {};
  const flat = asRecord(payload.booking) ?? payload;
  const ota = firstStr(bd.ota, bd.bookingSource, flat.ota, flat.channel) ?? 'OTA';
  const ref = firstStr(externalBookingId, bd.otaRefId, flat.otaRefId, bd.bookingNo, flat.bookingNo);
  return {
    label: `${ota} via AxisRooms`,
    ref,
    tone: TONE[BookingSource.AXIS_ROOMS]!,
    viaAxisRooms: true,
  };
}

export function SourceBadge({
  source,
  channelPayload,
  externalBookingId,
  className,
}: {
  source: string;
  channelPayload?: Payload | null | undefined;
  externalBookingId?: string | null | undefined;
  className?: string;
}) {
  const { label, ref, tone, viaAxisRooms } = resolveSource(source, channelPayload, externalBookingId);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
        tone,
        className,
      )}
    >
      {viaAxisRooms && <Globe size={11} aria-hidden="true" />}
      {label}
      {ref && <span className="font-mono font-normal opacity-80">· ref {ref}</span>}
    </span>
  );
}
